import Base from './base'

export default class GeneralAnalysis extends Base {
  list(params: object = {}) {
    return this.apiClient.get('general-analysis', params)
  }

  show(id: string) {
    return this.apiClient.get(`general-analysis/${id}`)
  }

  create(data: object = {}) {
    return this.apiClient.post('general-analysis', data)
  }

  // Find-or-create the prospect OPS write-up. POST because the first call
  // seeds the general_analyses row + sections + commentary.
  prospectWriteup(data: object = {}) {
    return this.apiClient.post('general-analysis/prospect-writeup', data)
  }

  // Find-or-create the client's Loan Committee Report. POST for the same
  // reason as prospectWriteup: the first call seeds the general_analyses row
  // and its sections.
  lcrReport(data: object = {}) {
    return this.apiClient.post('general-analysis/lcr-report', data)
  }

  updateStatus(id: string, data: object) {
    return this.apiClient.patch(`general-analysis/${id}/status`, data)
  }

  updateEffectiveDate(id: string, data: object) {
    return this.apiClient.patch(`general-analysis/${id}/effective-date`, data)
  }

  // Approves the Deal Summary, its submitted term structure, and closes the
  // committee's review task in one committee action.
  approveDeal(id: string, data: object = {}) {
    return this.apiClient.post(`general-analysis/${id}/approve-deal`, data)
  }

  // Changes the analysis type — the old draft terms are discarded and a
  // fresh (empty) draft of the new type is created and attached server-side.
  changeType(id: string, data: object) {
    return this.apiClient.patch(`general-analysis/${id}/type`, data)
  }

  updateAssignee(id: string, data: object) {
    return this.apiClient.patch(`general-analysis/${id}/assignee`, data)
  }

  updateSection(sectionId: string, data: object) {
    return this.apiClient.patch(`general-analysis/sections/${sectionId}`, data)
  }

  ensureExhibitThread(analysisId: string, data: object) {
    return this.apiClient.post(`general-analysis/${analysisId}/commentary/exhibit-thread`, data)
  }

  updateExhibit(analysisId: string, exhibitKey: string, data: object) {
    return this.apiClient.patch(
      `general-analysis/${analysisId}/exhibit/${encodeURIComponent(exhibitKey)}`,
      data,
    )
  }

  listEvents(analysisId: string) {
    return this.apiClient.get(`general-analysis/${analysisId}/events`)
  }

  // Keyed by client id — the Board Members exhibit reads the company's live CRM
  // People, not anything stored on the analysis. `analysisId` only decides
  // live-vs-frozen: once that analysis has Executed the server returns the
  // roster captured at execution instead of today's.
  listBoardMembers(clientId: string, analysisId?: string) {
    return this.apiClient.get(
      `general-analysis/client/${clientId}/board-members`,
      analysisId ? { analysisId } : {},
    )
  }

  listCommentary(analysisId: string) {
    return this.apiClient.get(`general-analysis/${analysisId}/commentary`)
  }

  addCommentary(analysisId: string, data: object | FormData) {
    return this.apiClient.post(`general-analysis/${analysisId}/commentary`, data)
  }

  updateCommentary(analysisId: string, commentaryId: string, data: object | FormData) {
    return this.apiClient.patch(`general-analysis/${analysisId}/commentary/${commentaryId}`, data)
  }

  uploadCommentaryInlineImage(analysisId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    return this.apiClient.post(`general-analysis/${analysisId}/commentary-inline-files`, formData)
  }

  deleteCommentary(analysisId: string, commentaryId: string) {
    return this.apiClient.delete(`general-analysis/${analysisId}/commentary/${commentaryId}`)
  }

  saveCommentaryLayout(analysisId: string, data: object) {
    return this.apiClient.put(`general-analysis/${analysisId}/commentary-layout`, data)
  }

  regenerateExport(analysisId: string) {
    return this.apiClient.post(`general-analysis/${analysisId}/regenerate-export`)
  }

  getBoxFolder(analysisId: string) {
    return this.apiClient.get(`general-analysis/${analysisId}/box-folder`)
  }

  // Multipart: `files[signedAmendment]` and/or `files[boardConsent]`.
  uploadSignedDocuments(analysisId: string, data: FormData) {
    return this.apiClient.post(`general-analysis/${analysisId}/signed-documents`, data)
  }

  saveView(id: string, data: object) {
    return this.apiClient.patch(`general-analysis/${id}/saved-view`, data)
  }
}
